import React, { useRef } from 'react';

import { isNotEmptyArray, cx } from '@travel/utils';
import { Filter, ArrowDown, ArrowUp } from '@travel/icons/ui';
import { FlatButton } from '@travel/ui';
import {
  MOBILE_SCREEN,
  useDeviceType,
  useDialogHandler,
  useOutsideClick,
} from '@travel/traveler-core/hooks';
import Dialog from '@travel/traveler-core/components/Dialog';
import ProviderFilterGroup from '@travel/traveler-core/components/ProviderFilterGroup';
import useRatBindClickComponent from '@travel/traveler-core/hooks/useRatBindClickComponent';

import type { Group } from 'favoriteList/constants/filters';
import { LinkWithRef } from 'favoriteList/core/universalRouter/Link/LinkWithRef';
import { getRatId, APPEAR_FILTER, CLICK_FILTER } from 'favoriteList/components/DataLayer/helper';

import styles from './filterButton.module.scss';

type Props = {
  // Filter options
  filters: Array<Group>;
  // Callback to submit filters
  onSearch?: () => void;
  // Callback to clear all filters
  onClear?: () => void;
  // Callback to be called when filter has been updated
  onChange?: (groupId: string, featureId: string) => void;
  // Flag if any feature is selected
  isFeatureSelected?: boolean;
  // flag for plan list
  isPlanList: boolean;
  // function to update member rank
  updateMemberRank: () => void;
};

function FilterButton(props: Props) {
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const { isOpen, onClose, onToggle } = useDialogHandler(false);

  const filterButtonRef = useRef(null);
  const filterWindowRef = useRef(null);
  const ratLink = useRef<HTMLAnchorElement>(null);

  useRatBindClickComponent(ratLink);
  useRatBindClickComponent(filterButtonRef);
  useOutsideClick(filterWindowRef, filterButtonRef, onToggle);

  const handleOnSearch = () => {
    props.onSearch?.();
    onClose();
  };

  const handleOnClear = () => {
    props.onClear?.();
  };

  const handleOnClickFilterButton = () => {
    ratLink.current?.click();
    props.updateMemberRank();
    onToggle();
  };

  const filterContent = props.filters?.map((item) => {
    if (isNotEmptyArray(item.features)) {
      return (
        <ProviderFilterGroup
          key={item.groupId}
          className={styles.filterBox}
          titleClassName={styles.filterBoxTitle}
          showMoreButtonClassName={styles.showMore}
          optionTextClassName={styles.optionText}
          id={item.groupId}
          title={item.groupName}
          options={item.features}
          onChange={props.onChange}
          maxVisibleCount={item.noInitialDisp || item.features.length}
          showAllLabel={
            <div className={styles.labelWrapper}>
              <span>もっと見る</span>
              <ArrowDown size={16} color="darkCyan" />
            </div>
          }
          showLessLabel={
            <div className={styles.labelWrapper}>
              <span>閉じる</span>
              <ArrowUp size={16} color="darkCyan" />
            </div>
          }
        />
      );
    }
  });

  return (
    <>
      <button
        ref={filterButtonRef}
        className={cx(
          styles.FilterButtonWrapper,
          isOpen && styles.isPopupOpen,
          props.isFeatureSelected && styles.isFeatureSelected,
        )}
        onClick={handleOnClickFilterButton}
        data-testid="filterButton"
        data-ratevent="appear"
        data-ratParam="all"
        data-ratid={getRatId(APPEAR_FILTER, props.isPlanList, isMobile)}
      >
        <Filter size={14} color={isOpen ? 'white' : 'default'} />
        {props.isFeatureSelected && isMobile ? null : '絞り込み'}
      </button>
      {isOpen &&
        (isMobile ? (
          <Dialog
            title={'絞り込み'}
            isOpen={isOpen}
            onClose={onClose}
            contentClassName={styles.dialogContentBox}
            buttonLabelPrimary={'絞り込む'}
            onClickPrimary={handleOnSearch}
            isUseContentFlexBox={false}
            hasAnimation={true}
          >
            {filterContent}
          </Dialog>
        ) : (
          <div className={styles.filterContentWrapper} ref={filterWindowRef}>
            <div className={styles.filterContent}>{filterContent}</div>
            <div className={styles.actionBox}>
              <FlatButton
                onClick={handleOnClear}
                className={styles.actionButton}
                classType="secondary"
                data-testid="filterButton-clear-button"
              >
                全て解除
              </FlatButton>
              <FlatButton
                onClick={handleOnSearch}
                className={styles.actionButton}
                data-testid="filterButton-search-button"
              >
                絞り込む
              </FlatButton>
            </div>
          </div>
        ))}
      <LinkWithRef
        data-ratevent="click"
        data-ratParam="all"
        data-ratid={getRatId(CLICK_FILTER, props.isPlanList, isMobile)}
        ref={ratLink}
        to={''}
      >
        {null}
      </LinkWithRef>
    </>
  );
}

export default FilterButton;
